import React, { forwardRef } from 'react';

import {
  Breakpoints,
  RadioButton,
  TextAppearance,
  TextColor,
  TextCustom,
  TextLineHeight,
  TextSize,
  TextWeight,
} from '@vw-marketing/us-components';

import { ImageContainer } from './ImageContainer';
import {
  StyledGarageVehicleCardContainer,
  StyledHeaderWrapper,
  StyledVehicleInformationContainer,
} from './styles';
import { useImageValidator } from '../../../hooks/use-image-validator';
import { Vehicles } from '../../../typings/main';
import { applyTextTransformations } from '../../../utils/applyTextTransformations';

interface GarageVehicleCardViewProps {
  vehicle: Vehicles;
  isSelected: boolean;
  onSelect: (vehicleId: string) => void;
  translationHandler: (key: string) => string;
}

export const GarageVehicleCardView = forwardRef<HTMLDivElement, GarageVehicleCardViewProps>(
  ({ vehicle, isSelected, onSelect, translationHandler }, ref) => {
    const { isValidUrl, imgUrl } = useImageValidator({
      imageUrls: [
        vehicle?.representativeImgURLComplete,
        vehicle?.representativeImgURLPartial,
      ],
      dependencies: [vehicle],
    });

    const vehicleInformation: string = `${vehicle.modelYear || ''} ${vehicle.modelName || ''}`;
    const vin: string = vehicle.vin || '';

    const handleSelectVehicle = () => {
      onSelect(vehicle.vehicleId);
    };

    return (
      <StyledGarageVehicleCardContainer
        hasImage={isValidUrl}
        isSelected={isSelected}
        onClick={handleSelectVehicle}
        ref={ref}
      >
        <StyledHeaderWrapper>
          <StyledVehicleInformationContainer>
            <TextCustom
              appearance={TextAppearance.headline300}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize20,
                  fontWeight: TextWeight.bold,
                  color: TextColor.primary,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
                [Breakpoints.b1600]: {
                  fontSize: TextSize.TextSize24,
                  lineHeight: TextLineHeight.TextLineHeight28,
                },
              }}
            >
              {applyTextTransformations(vehicleInformation, [vehicleInformation])}
            </TextCustom>
            <TextCustom
              appearance={TextAppearance.copy100}
              style={{
                [Breakpoints.default]: {
                  fontSize: TextSize.TextSize14,
                  fontWeight: TextWeight.light,
                  color: TextColor.primary,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
                [Breakpoints.b1600]: {
                  fontSize: TextSize.TextSize16,
                  lineHeight: TextLineHeight.TextLineHeight24,
                },
              }}
            >
              {applyTextTransformations(
                `${translationHandler('vehicleManagement.interactive.card.vehicle.header.VIN')} ${vin}`,
                [translationHandler('vehicleManagement.interactive.card.vehicle.header.VIN'), vin],
              )}
            </TextCustom>
          </StyledVehicleInformationContainer>
          <RadioButton
            value={vehicle.vehicleId}
            name="vehicle-selection"
            checked={isSelected}
            onChange={handleSelectVehicle}
          />
        </StyledHeaderWrapper>
        {isValidUrl && <ImageContainer imgSource={imgUrl} />}
      </StyledGarageVehicleCardContainer>
    );
  },
);

GarageVehicleCardView.displayName = 'GarageVehicleCardView';
